import React, { useState } from "react";
import useAnnouncements from "../hooks/useAnnouncements";
import "../styles/AnnouncementsFloatingWidget.css";

// inline: when true, renders compact inline button + dropdown panel (for navbar placement)
const AnnouncementsFloatingWidget = ({
  maxItems = null, // null = show all
  inline = false,
  pollWhenOpen = 30000,
}) => {
  const [open, setOpen] = useState(false);

  const {
    announcements,
    loading,
    error,
    refresh,
    markRead,
    markAllRead,
    readIds,
    unreadCount,
  } = useAnnouncements({ limit: 100, useCache: true, autoFetch: true });

  // Start polling when the panel is open so announcements stay current
  React.useEffect(() => {
    if (!open) return;

    // Fetch fresh data immediately when opened
    (async () => {
      try {
        await refresh();
      } catch {
        // ignore
      }
    })();

    if (!pollWhenOpen || pollWhenOpen <= 0) return;

    const interval = setInterval(() => {
      refresh();
    }, pollWhenOpen);

    return () => clearInterval(interval);
  }, [open, pollWhenOpen, refresh]);

  const containerClass = inline ? "afw-inline" : "afw-container";

  const itemsToShow =
    typeof maxItems === "number" ? announcements.slice(0, maxItems) : announcements;

  return (
    <div className={`${containerClass} ${open ? "afw-open" : ""}`}>
      <div
        className={`${inline ? "afw-button-inline" : "afw-button"}`}
        onClick={() => setOpen((s) => !s)}
        aria-label="Open announcements"
      >
        <span className="afw-bell">📢</span>
        {unreadCount > 0 && <span className="afw-badge">{unreadCount}</span>}
      </div>

      <div
        className={`afw-panel ${inline ? "afw-panel-inline" : ""}`}
        role="dialog"
        aria-hidden={!open}
      >
        <div className="afw-panel-header">
          <strong>Announcements</strong>
          <div className="afw-actions">
            <button className="afw-action" onClick={() => refresh()} title="Refresh">
              ⟳
            </button>
            <button className="afw-action" onClick={() => markAllRead()} title="Mark all as read">
              ✓ All
            </button>
            <button className="afw-close" onClick={() => setOpen(false)} aria-label="Close">
              ✕
            </button>
          </div>
        </div>

        <div className="afw-panel-body">
          {loading && <div className="afw-empty">Loading...</div>}
          {error && <div className="afw-error">{error}</div>}

          {!loading && !error && itemsToShow.length === 0 && (
            <div className="afw-empty">No announcements</div>
          )}

          {!loading && !error && itemsToShow.length > 0 && (
            <ul className="afw-list">
  {itemsToShow.map((a, idx) => {
  const isRead = readIds.has(a.id);

  return (
    <li
      key={a.id ?? `row-${idx}`}
      className={`afw-item ${isRead ? "afw-read" : "afw-unread"}`}
    >
      {/* SINGLE INLINE ROW */}
<div className="afw-item-header-row">
  <div className="afw-item-title">
    {a.title || "Announcement"}
  </div>

  <div className="afw-item-header-right">
    <div className="afw-item-meta-severity">
      <span className="afw-item-meta">
        {a.date_created
          ? new Date(a.date_created).toLocaleDateString()
          : ""}
      </span>

      <span
        className={`afw-severity-btn afw-severity-${(a.severity || "info").toLowerCase()}`}
      >
        {(a.severity || "info").toUpperCase()}
      </span>
    </div>

    <button
      className={`afw-markread-icon ${isRead ? "afw-read-icon" : ""}`}
      onClick={() => markRead(a.id)}
      disabled={isRead}
      title={isRead ? "Read" : "Mark as read"}
    >
      ✓
    </button>
  </div>
</div>


      <div className="afw-item-body">
        {a.content}
      </div>
    </li>
  );
})}

            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnnouncementsFloatingWidget;


